/* iceberg-ui */
import {
    Box,
    Button,
    Flex,
    LoadingSpinner,
    Text
} from '@auviknetworks/iceberg-ui';

/* MUI */
import { Checkbox } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/* Shared */
import { Helpers, Notify, SessionHelper } from 'application/Shared/Helpers';
import { Env, debug } from 'application/Shared/Utils';

/* Hooks */
import { usePutStartAnmTrial } from 'hooks/dataAccess/ANM/startTrial';
import { usePutUserSelf } from 'hooks/dataAccess/User/user';
import { useSession } from 'hooks/redux/Selectors/session';

/* React */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

/* Components */
import { ANMTrialContainer } from './internal/ANMTrialContainer';
import { ANMTrialFooter } from './internal/ANMTrialFooter';
import { RightWelcomePanel } from './internal/RightWelcomePanel';

const LoadingScreen = () => (
    <Flex flexDirection="column" textAlign="center" m={4}>
        <LoadingSpinner loadingText="" justifyContent="center" my={4} />
    </Flex>
);

const WelcomeScreen = ({ createTrial }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [isChecked, setChecked] = useState(false);

    return (
        <>
            <Flex alignItems="center" flexDirection="column" mt={4} mx="4%">
                <Text
                    fontSize={4}
                    color={theme.palette.anmTrial.titleTextColor}
                    sx={{
                        textTransform: 'uppercase'
                    }}
                    textAlign="center"
                >
                    {t('anmTrial.welcome.title')}
                </Text>
                <Flex flexDirection="column" mt="8%" color="">
                    <Text
                        color={theme.palette.anmTrial.primaryTextColor}
                        fontSize={[3, 3, 3, 4]}
                        mb="4%"
                    >
                        {t('anmTrial.welcome.description.1')}
                    </Text>
                    <Text
                        color={theme.palette.anmTrial.primaryTextColor}
                        fontSize={[3, 3, 3, 4]}
                        mb="4%"
                        dangerouslySetInnerHTML={{
                            __html: t('anmTrial.welcome.description.2')
                        }}
                    />
                    <Text
                        color={theme.palette.anmTrial.primaryTextColor}
                        fontSize={[3, 3, 3, 4]}
                        mb="4%"
                        dangerouslySetInnerHTML={{
                            __html: t('anmTrial.welcome.description.3')
                        }}
                    />
                    <Flex flexDirection="row" mt={4}>
                        <Box mr={3}>
                            <Checkbox
                                checked={isChecked}
                                onChange={() => setChecked(!isChecked)}
                                data-testid="agreeToANMTermsCheckbox"
                                variant="secondary"
                            />
                        </Box>
                        <Text
                            alignSelf="center"
                            mx={2}
                            dangerouslySetInnerHTML={{
                                __html: t('anmTrial.welcome.tos')
                            }}
                        />
                    </Flex>
                </Flex>
                <Button
                    mt={5}
                    mb={6}
                    onClick={createTrial}
                    disabled={!isChecked}
                    size="large"
                    data-testid="createANMTrialButton"
                    data-trackid="partner_anmTrial_letsStart"
                >
                    {t('anmTrial.welcome.letsStartButton')}
                </Button>
            </Flex>
            <ANMTrialFooter />
        </>
    );
};

const CreatingTrial = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    return (
        <>
            <Flex alignItems="center" flexDirection="column" mt={4} mx="4%">
                <Text
                    fontSize={['32px', '32px', '32px', 5]}
                    color={theme.palette.anmTrial.titleTextColor}
                    textAlign="center"
                >
                    {t('anmTrial.welcome.title')}
                </Text>
                <LoadingSpinner
                    loadingText={t('Common.loading').toUpperCase()}
                    my={6}
                    data-testid="creatingTrialSpinner"
                />
            </Flex>
            <ANMTrialFooter />
        </>
    );
};

const ErrorScreen = () => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        // TODO: When removing feature flag, change this Flex to MUI Box, and
        // just leave the data-testid.
        <Flex
            flexDirection="column"
            alignItems="center"
            data-testid="errorAnmTrial"
            my={0}
        >
            <Flex
                flexDirection="column"
                textAlign="center"
                bg={theme.palette.anmTrial.errorScreen.backgroundColor}
                borderStyle="solid"
                borderColor={theme.palette.anmTrial.errorScreen.borderColor}
                borderWidth={1}
                m={5}
                p={4}
                data-testid="errorAnmTrialMessage"
            >
                <Text
                    textStyle="header"
                    color={theme.palette.anmTrial.errorScreen.textColor}
                >
                    {t('anmTrial.error.header')}
                </Text>
                <Text
                    color={theme.palette.anmTrial.errorScreen.textColor}
                    my={4}
                    fontSize={16}
                    data-testid="errorAnmTrialMessageBody"
                >
                    {t('anmTrial.error.body')}
                </Text>
                <Button
                    as="a"
                    href="mailto:sales@auvik.com"
                    alignSelf="center"
                    style={{ textDecoration: 'none' }}
                    data-testid="errorAnmTrialContactSupportBtn"
                    data-trackid="errorAnmTrialContactSupportBtn"
                >
                    {t('anmTrial.error.support')}
                </Button>
            </Flex>
        </Flex>
    );
};

const redirectToAnm = () => {
    const oktaAppLink = Env.get('REACT_APP_ANM_OKTA_APP_LINK');
    window.location.assign(oktaAppLink);
};

export const ANMTrial = () => {
    const { t } = useTranslation();
    const session = useSession();
    const navigate = useNavigate();

    const [loadingTrialData, setLoadingTrialData] = useState(true);
    const [creatingTrial, setCreatingTrial] = useState(false);
    const [errorCreatingTrial, setErrorCreatingTrial] = useState(false);

    const sessionHelper = new SessionHelper(session);
    const anmTenantReservedStatus = sessionHelper.getAnmTenantReservedStatus();
    const reservedStatusError = anmTenantReservedStatus?.error;
    const reservedInfo = anmTenantReservedStatus?.reservedInfo;
    const isReserved = !!reservedInfo?.reserved;

    const tenantId = sessionHelper.getTenantId();

    const { putStartAnmTrial } = usePutStartAnmTrial({
        platformTenantId: tenantId
    });

    const { putUserSelf } = usePutUserSelf();

    // This check essentially qualifies the user/tenant to see if they should be able to view the trial page.
    useEffect(() => {
        if (loadingTrialData) {
            // If there was an error loading the tenant's trial data, they most likely don't have an ANM-associated tenant, so redirect to home.
            if (reservedStatusError) {
                navigate('/');
                return;
            }

            // If the tenant already has an unreserved ANM tenant, forward them to ANM.
            if (!isReserved) {
                redirectToAnm();
                return;
            }

            // Otherwise, remove the loading page and show them the trial creation wizard.
            setLoadingTrialData(false);
        }
    }, [isReserved, loadingTrialData, navigate, reservedStatusError]);

    const handleCreateTrial = async () => {
        setCreatingTrial(true);

        try {
            // Save that they accepted the ToS. (they had to check the box in order to click the button to call this function.)
            await putUserSelf({ params: { tos: true } });

            // start their trial and send any potential marketing params with the request
            // so they can be forwarded along to marketing (on successful trial creation).
            const marketingData = Helpers.getUTMFieldsFromURL(
                window.location.href
            );
            await putStartAnmTrial(marketingData);

            // Redirect user to ANM Okta app link
            redirectToAnm();
        } catch (e) {
            debug.e(e.toString());
            Notify.e(t('anmTrial.welcome.createTrial.errorNotification'));
            setErrorCreatingTrial(true);
            setCreatingTrial(false);
        }
    };

    if (loadingTrialData) {
        return <LoadingScreen />;
    }

    let leftPanel = <WelcomeScreen createTrial={handleCreateTrial} />;

    if (creatingTrial) {
        leftPanel = <CreatingTrial />;
    }

    // Error creating trial
    if (errorCreatingTrial) {
        leftPanel = <ErrorScreen />;
    }

    return (
        <ANMTrialContainer
            leftPanel={leftPanel}
            rightPanel={<RightWelcomePanel error={errorCreatingTrial} />}
        />
    );
};
